/**
 * useReplayRecorder Hook
 *
 * Manages replay recording for mfersbeheaded game.
 * Extracted from Board.js as part of refactoring effort.
 *
 * @param gamePlayUUID - Unique ID for this gameplay session
 * @param levelConfig - Level configuration
 * @param userId - User ID from new auth system (if logged in)
 */

import { useRef, useEffect, useCallback } from 'react';
import ReplayRecorder from '../../../services/replayRecorder';
import logger from '../../../utils/logger';
import CONSTANTS from '../constants';

interface LevelConfig {
  LEVEL_ID: string;
  LEVEL_NAME: string;
  BOARD_SIZE: number;
}

interface SessionData {
  id: string;
  level_id: string;
  level_name: string;
  game_state: number;
  player_name?: string;
  user_id?: string;
}

interface StateChange {
  t: number;
  changes: Array<[number, number, number]>;
}

interface FinalData {
  final_score: number;
  final_moves: number;
  game_state: number;
  duration_ms: number;
  final_board?: number[][];
}

interface FinalizeResult {
  success: boolean;
  score_id: string | null;
  validated_score: number | null;
  validation_errors: string[] | null;
}

export const useReplayRecorder = (
  gamePlayUUID: string,
  levelConfig: LevelConfig,
  userId: string | null = null
) => {
  const { LEVEL_ID, LEVEL_NAME, BOARD_SIZE } = levelConfig;
  const replayRecorderRef = useRef<ReplayRecorder | null>(null);

  /**
   * Create a replay session
   */
  const createReplaySession = useCallback(async (
    playerName: string | null,
    initialBoardChanges: Array<[number, number, number]>
  ): Promise<boolean> => {
    if (replayRecorderRef.current) {
      logger.debug('Replay recorder already exists', null, 'replay');
      return false;
    }

    // Session data for server
    const sessionData: SessionData = {
      id: gamePlayUUID,
      level_id: LEVEL_ID,
      level_name: LEVEL_NAME,
      game_state: CONSTANTS.GAME_STATE_NOT_YET_BEGUN,
    };

    // Only add player_name if it exists
    if (playerName) {
      sessionData.player_name = playerName;
    }

    // Add user_id from new auth system
    if (userId) {
      sessionData.user_id = userId;
    }

    // Create replay recorder instance
    replayRecorderRef.current = new ReplayRecorder(gamePlayUUID, sessionData);

    // Record initial board state (buffered in memory)
    if (initialBoardChanges && initialBoardChanges.length > 0) {
      console.log('=== useReplayRecorder - recordBoardInit DEBUG ===');
      console.log('Received initialBoardChanges:', initialBoardChanges.length, 'changes');
      console.log('First 10 changes:', initialBoardChanges.slice(0, 10));
      console.log('Board size:', BOARD_SIZE);
      console.log('===============================================');
      replayRecorderRef.current.recordBoardInit(initialBoardChanges, BOARD_SIZE);
    }

    // Create session on server immediately
    // If this fails, recording will continue in memory but won't be saved to server
    try {
      await replayRecorderRef.current.createSession();
      // Start periodic flush timer after session is created
      replayRecorderRef.current.startPeriodicFlush();
      logger.debug('Replay session created on server', { sessionId: gamePlayUUID }, 'replay');
      return true;
    } catch (error) {
      logger.error('Failed to create replay session on server, recording disabled', { error: (error as Error).message }, 'replay');
      // Clean up recorder since session creation failed
      replayRecorderRef.current = null;
      return false;
    }
  }, [gamePlayUUID, LEVEL_ID, LEVEL_NAME, BOARD_SIZE, userId]);

  /**
   * Record a swap event
   */
  const recordSwap = useCallback((
    elapsedTime: number,
    fromPos: [number, number],
    toPos: [number, number],
    isValid: boolean,
    fromValue?: number | null,
    toValue?: number | null
  ) => {
    if (replayRecorderRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (replayRecorderRef.current as any).recordSwap(elapsedTime, fromPos, toPos, isValid, fromValue, toValue);
    }
  }, []);

  /**
   * Record a power-up activation
   */
  const recordPowerUp = useCallback((
    elapsedTime: number,
    position: [number, number],
    clearedCount: number
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordPowerUp(elapsedTime, position, clearedCount);
    }
  }, []);

  /**
   * Record board state changes
   */
  const recordBoardState = useCallback((
    elapsedTime: number,
    stateChanges: Array<[number, number, number]>,
    changeType: string = 'sweep'
  ) => {
    if (replayRecorderRef.current && stateChanges.length > 0) {
      logger.debug(`Recording ${changeType} changes to replay`, { count: stateChanges.length }, 'replay');
      replayRecorderRef.current.recordBoardState(elapsedTime, stateChanges, changeType);
    } else {
      logger.debug(`NOT recording ${changeType}`, {
        hasRecorder: !!replayRecorderRef.current,
        changeCount: stateChanges?.length || 0
      }, 'replay');
    }
  }, []);

  /**
   * Record multiple board state changes
   */
  const recordBoardStateList = useCallback((
    stateChangesList: StateChange[],
    changeType: string = 'sweep'
  ) => {
    if (replayRecorderRef.current && stateChangesList.length > 0) {
      logger.debug(`Recording ${stateChangesList.length} ${changeType} batches to replay`, null, 'replay');
      stateChangesList.forEach(stateChange => {
        replayRecorderRef.current!.recordBoardState(
          stateChange.t,
          stateChange.changes,
          changeType
        );
      });
    }
  }, []);

  /**
   * Record a score update
   */
  const recordScoreUpdate = useCallback((
    elapsedTime: number,
    newScore: number,
    points: number,
    reason: string
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordScoreUpdate(elapsedTime, newScore, points, reason);
    }
  }, []);

  /**
   * Record game state change
   */
  const recordGameState = useCallback((
    elapsedTime: number,
    gameState: number,
    movesUsed: number,
    duration: number
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordGameState(elapsedTime, gameState, movesUsed, duration);
    }
  }, []);

  /**
   * Record barrel placement
   */
  const recordBarrelPlace = useCallback((
    elapsedTime: number,
    barrelChanges: Array<[number, number, number]>
  ) => {
    if (replayRecorderRef.current && barrelChanges.length > 0) {
      replayRecorderRef.current.recordBarrelPlace(elapsedTime, barrelChanges);
    }
  }, []);

  /**
   * Update the session (e.g., mark as in progress)
   */
  const updateSession = useCallback((updates: Record<string, unknown>) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.updateSession(updates);
    }
  }, []);

  /**
   * Finalize the replay
   */
  const finalizeReplay = useCallback(async (finalData: FinalData): Promise<FinalizeResult> => {
    if (replayRecorderRef.current) {
      return await replayRecorderRef.current.finalize(finalData) as FinalizeResult;
    }
    return { success: false, score_id: null, validated_score: null, validation_errors: ['No replay recorder'] };
  }, []);

  /**
   * Record cells being cleared
   */
  const recordClear = useCallback((
    elapsedTime: number,
    clearedCells: Array<[number, number, number]>,
    reason: string = 'match'
  ) => {
    if (replayRecorderRef.current && clearedCells.length > 0) {
      replayRecorderRef.current.recordClear(elapsedTime, clearedCells, reason);
    }
  }, []);

  /**
   * Record gravity movements
   */
  const recordGravity = useCallback((
    elapsedTime: number,
    movements: Array<{ from: [number, number]; to: [number, number]; value: number }>
  ) => {
    if (replayRecorderRef.current && movements.length > 0) {
      replayRecorderRef.current.recordGravity(elapsedTime, movements);
    }
  }, []);

  /**
   * Record new pieces being added
   */
  const recordFill = useCallback((
    elapsedTime: number,
    newPieces: Array<[number, number, number]>
  ) => {
    if (replayRecorderRef.current && newPieces.length > 0) {
      replayRecorderRef.current.recordFill(elapsedTime, newPieces);
    }
  }, []);

  /**
   * Check if replay recorder is initialized
   */
  const isRecorderReady = useCallback((): boolean => {
    return !!replayRecorderRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Note: We don't finalize here as that's handled by the component
      // This is just cleanup
      if (replayRecorderRef.current) {
        logger.debug('Cleaning up replay recorder', null, 'replay');
      }
    };
  }, []);

  return {
    // Session management
    createReplaySession,
    updateSession,
    finalizeReplay,
    isRecorderReady,

    // Event recording
    recordSwap,
    recordPowerUp,
    recordBoardState,
    recordBoardStateList,
    recordScoreUpdate,
    recordGameState,
    recordBarrelPlace,

    // Enhanced event recording
    recordClear,
    recordGravity,
    recordFill,

    // Direct ref access (use sparingly)
    replayRecorderRef,
  };
};

export default useReplayRecorder;
